import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2, Play, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const dtt = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const LABEL = { allarme: "Allarme o guasto", offline: "Offline di giorno", produzione: "Produzione bassa", batteria: "Batteria anomala", rientrato: "Allarme rientrato", meteo: "Allerta meteo", posizione: "Senza posizione",
  proposte_settimana: "Proposte senza riscontro", commessa: "Stato commessa", listino: "Novità a listino", vesta_world: "Menzioni e messaggi", punti: "Punti Vesta", buongiorno: "Buongiorno di Vesta", report_mensile: "Report mensile" };
const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const SOGLIE = [["offline_ore", "Offline di giorno da (ore)"], ["produzione_pct", "Produzione sotto il (% del previsto)"], ["batteria_giorni", "Batteria non in carica da (giorni)"],
  ["vento_kmh", "Vento forte oltre (km/h)"], ["meteo_ora", "Controllo meteo di domani alle (ora)"], ["posizione_ora", "Controllo impianti senza posizione alle (ora)"],
  ["produzione_ora", "Controllo produzione dalle (ora)"], ["batteria_ora", "Controllo batterie dalle (ora)"], ["riepilogo_ora", "Riepilogo email dalle (ora)"],
  ["buongiorno_ora", "Buongiorno di Vesta alle (ora)"], ["settimanale_giorno", "Resoconto proposte il giorno (0 = lunedì … 6 = domenica)"], ["proposte_giorni_inviata", "Proposta inviata e mai aperta da (giorni)"],
  ["proposte_giorni_aperta", "Proposta aperta e non firmata da (giorni)"], ["report_giorno", "Report mensile il giorno del mese"]];

function Registro({ tick }) {
  const [f, setF] = useState({ tipo: "", partner_id: "", dal: "", al: "", letta: "", page: 1 });
  const [d, setD] = useState(null);
  const [partners, setPartners] = useState([]);
  const load = useCallback(() => api.get("/comunicazioni-vesta/registro", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setD(r.data)), [f]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { api.get("/comunicazioni-vesta/preferenze").then((r) => setPartners([...new Map(r.data.utenti.filter((u) => u.partner_id).map((u) => [u.partner_id, u.partner])).entries()])); }, []);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={f.tipo} onChange={up("tipo")} className={sel} data-testid="com-reg-tipo"><option value="">Tutti i tipi</option>{d && Object.entries(d.tipi).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={f.partner_id} onChange={up("partner_id")} className={sel} data-testid="com-reg-partner"><option value="">Tutti i Partner</option>{partners.map(([id, n]) => <option key={id} value={id}>{n || id}</option>)}</select>
        <Input type="date" value={f.dal} onChange={up("dal")} className="h-9 w-40" data-testid="com-reg-dal" />
        <Input type="date" value={f.al} onChange={up("al")} className="h-9 w-40" data-testid="com-reg-al" />
        <select value={f.letta} onChange={up("letta")} className={sel} data-testid="com-reg-letta"><option value="">Lette e non lette</option><option value="si">Lette</option><option value="no">Non lette</option></select>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="com-reg-last">Ultimo controllo: {dtt(d?.ultimo_controllo)}</span>
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="com-reg-table">
            <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground"><tr><th className="p-2">Data</th><th className="p-2">Tipo</th><th className="p-2">Partner</th><th className="p-2">Comunicazione</th><th className="p-2">Lette</th><th className="p-2">Email</th></tr></thead>
            <tbody>{d.items.map((x) => (
              <tr key={x.id} className="border-t border-border align-top" data-testid={`com-reg-row-${x.id}`}>
                <td className="whitespace-nowrap p-2 text-xs">{dtt(x.at)}</td><td className="p-2 text-xs">{d.tipi[x.tipo]}{x.test && " · prova"}</td><td className="p-2 text-xs">{x.partner_name || "—"}</td>
                <td className="p-2"><div className="font-medium">{x.titolo}</div><div className="text-xs text-muted-foreground">{x.testo}</div></td>
                <td className="whitespace-nowrap p-2 text-xs">{x.lette}/{x.destinatari}</td><td className="p-2 text-xs">{x.email_inviate}</td>
              </tr>))}
              {!d.items.length && <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground" data-testid="com-reg-empty">Nessuna comunicazione.</td></tr>}</tbody>
          </table>
        </div>)}
      {d && d.totale > 50 && <div className="flex items-center justify-end gap-2 text-xs">
        <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="com-reg-prev">‹</Button>
        <span>Pagina {f.page} di {Math.ceil(d.totale / 50)}</span>
        <Button size="sm" variant="outline" disabled={f.page * 50 >= d.totale} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="com-reg-next">›</Button>
      </div>}
    </div>
  );
}

function Impostazioni() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/comunicazioni-vesta/settings").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async () => {
    setBusy(true);
    try { await api.put("/comunicazioni-vesta/settings", { tipi: d.tipi, soglie: d.soglie }); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="space-y-2 p-4"><div className="text-sm font-semibold">Interruttore generale per tipo</div>
        {[...new Set(Object.values(d.meta).map((m) => m.gruppo))].map((g) => (
          <div key={g} className="space-y-2 pt-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary"><span className="flex-1">{g}</span><span className="font-normal normal-case text-muted-foreground">Tutto il gruppo</span>
              <Switch checked={Object.entries(d.meta).some(([k, m]) => m.gruppo === g && d.tipi[k].attivo)} data-testid={`com-glob-gruppo-${g}`}
                onCheckedChange={(x) => setD({ ...d, tipi: { ...d.tipi, ...Object.fromEntries(Object.entries(d.meta).filter(([, m]) => m.gruppo === g).map(([k]) => [k, { attivo: x }])) } })} /></div>
        {Object.entries(d.meta).filter(([, m]) => m.gruppo === g).map(([k, m]) => (
          <label key={k} className="flex items-center gap-3 rounded-md border border-border p-2.5" data-testid={`com-glob-${k}`}>
            <span className="flex-1"><span className="block text-sm">{m.titolo}{m.urgente && <span className="ml-2 text-[10px] font-bold uppercase text-red-400">urgente</span>}</span><span className="text-xs text-muted-foreground">{m.desc}</span></span>
            <Switch checked={d.tipi[k].attivo} onCheckedChange={(x) => setD({ ...d, tipi: { ...d.tipi, [k]: { attivo: x } } })} data-testid={`com-glob-switch-${k}`} />
          </label>))}
          </div>))}
      </Card>
      <Card className="space-y-2 p-4"><div className="text-sm font-semibold">Soglie e orari (ora di Roma)</div>
        {SOGLIE.map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm"><span className="flex-1">{l}</span>
            <Input type="number" min={0} value={d.soglie[k]} onChange={(e) => setD({ ...d, soglie: { ...d.soglie, [k]: e.target.value } })} className="h-8 w-24" data-testid={`com-soglia-${k}`} /></label>))}
        <p className="text-[11px] text-muted-foreground">Valori predefiniti per i nuovi utenti: tutte le comunicazioni attive, email per le urgenti (allarmi, batteria), silenzio 20:00 → 07:30.</p>
      </Card>
      <div className="lg:col-span-2"><Button onClick={save} disabled={busy} data-testid="com-settings-save"><Save className="mr-2 h-4 w-4" />Salva impostazioni</Button></div>
    </div>
  );
}

function PartnerEdit({ p, meta, onDone }) {
  const [sosp, setSosp] = useState(new Set(p.sospesi));
  const gruppi = [...new Set(Object.values(meta).map((m) => m.gruppo))];
  const set = (keys, on) => { const s = new Set(sosp); keys.forEach((k) => (on ? s.delete(k) : s.add(k))); setSosp(s); };
  const save = async () => {
    try { await api.put(`/comunicazioni-vesta/partner/${p.id}`, { sospesi: [...sosp] }); toast.success(`Comunicazioni aggiornate per ${p.name}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3" data-testid={`com-partner-edit-${p.id}`}>
      {gruppi.map((g) => { const keys = Object.keys(meta).filter((k) => meta[k].gruppo === g); return (
        <div key={g} className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary"><span className="flex-1">{g}</span>
            <Switch checked={keys.some((k) => !sosp.has(k))} onCheckedChange={(x) => set(keys, x)} data-testid={`com-partner-gruppo-${p.id}-${g}`} /></label>
          <div className="grid gap-1 sm:grid-cols-2">{keys.map((k) => (
            <label key={k} className="flex items-center gap-2 rounded px-2 py-1 text-xs"><span className="flex-1">{meta[k].titolo}</span>
              <Switch checked={!sosp.has(k)} onCheckedChange={(x) => set([k], x)} data-testid={`com-partner-tipo-${p.id}-${k}`} /></label>))}</div>
        </div>); })}
      <Button size="sm" onClick={save} data-testid={`com-partner-save-${p.id}`}><Save className="mr-2 h-4 w-4" />Salva per {p.name}</Button>
    </div>
  );
}

function PerPartner() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/comunicazioni-vesta/partner").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <Card className="mb-4 space-y-2 p-4" data-testid="com-per-partner">
      <div className="text-sm font-semibold">Comunicazioni per Partner <span className="text-xs font-normal text-muted-foreground">· sospendi gruppi o tipi per un singolo Partner; le scelte dei suoi utenti restano salvate</span></div>
      {d.partner.map((p) => (
        <div key={p.id} className="space-y-2">
          <div className="flex items-center gap-3 text-sm" data-testid={`com-partner-row-${p.id}`}><span className="flex-1">{p.name}</span>
            <span className="text-xs text-muted-foreground">{p.sospesi.length ? `${p.sospesi.length} sospese` : "Tutte attive"}</span>
            <Button size="sm" variant="outline" onClick={() => setOpen(open === p.id ? null : p.id)} data-testid={`com-partner-open-${p.id}`}>Gestisci</Button></div>
          {open === p.id && <PartnerEdit p={p} meta={d.meta} onDone={() => { setOpen(null); load(); }} />}
        </div>))}
    </Card>
  );
}

function Preferenze() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/comunicazioni-vesta/preferenze").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  const rows = d.utenti.filter((u) => !q || `${u.partner} ${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const n = Object.keys(d.tipi).length;
  return (
    <div className="space-y-3">
      <Input placeholder="Cerca Partner o utente…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="com-pref-search" />
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="com-pref-table">
          <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground"><tr><th className="p-2">Partner</th><th className="p-2">Utente</th><th className="p-2">Attive</th><th className="p-2">Anche email</th><th className="p-2">Silenzio</th></tr></thead>
          <tbody>{rows.map((u) => (
            <tr key={u.id} className="border-t border-border" data-testid={`com-pref-row-${u.id}`}>
              <td className="p-2">{u.partner || "—"}</td><td className="p-2"><div>{u.name}</div><div className="text-xs text-muted-foreground">{u.email}{!u.personalizzate && " · predefinite"}</div></td>
              <td className="p-2 text-xs">{u.attive.length === n ? "Tutte" : u.attive.map((k) => d.tipi[k]).join(", ") || "Nessuna"}</td>
              <td className="p-2 text-xs">{u.con_email.map((k) => d.tipi[k]).join(", ") || "—"}</td>
              <td className="whitespace-nowrap p-2 text-xs">{u.silenzio.attivo ? `${u.silenzio.dal} → ${u.silenzio.al}` : "No"}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function ComunicazioniVesta() {
  const [busy, setBusy] = useState("");
  const [tipo, setTipo] = useState("allarme");
  const [k, setK] = useState(0);
  const prova = async (body) => {
    setBusy(body.modo);
    try {
      const r = await api.post("/comunicazioni-vesta/prova", body, { timeout: 300000 });
      if (body.modo === "esempio") toast.success("Comunicazione di prova inviata alla tua campanella Vesta");
      else { const e = r.data.esito || {}; toast.success(e.skipped || `Controllo eseguito: ${Object.entries(e).filter(([, v]) => v).map(([a, v]) => `${LABEL[a] || (a === "riepiloghi" ? "Riepiloghi email" : a)} ${v}`).join(", ") || "nessuna nuova comunicazione"}`); }
      setK((x) => x + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy("");
  };
  return (
    <div className="space-y-6" data-testid="comunicazioni-vesta-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1"><h1 className="flex items-center gap-2 text-3xl font-bold"><Bell className="h-7 w-7 text-primary" />Comunicazioni Vesta</h1>
          <p className="text-sm text-muted-foreground">Avvisi automatici di Vesta agli installatori sugli impianti: controllo ogni 15 minuti dopo la sincronizzazione FIER.</p></div>
        <Button variant="outline" onClick={() => prova({ modo: "esegui" })} disabled={!!busy} data-testid="com-prova-esegui">{busy === "esegui" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}Prova ora</Button>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={sel} data-testid="com-prova-tipo">
          {Object.entries(LABEL).map(([t, l]) => <option key={t} value={t}>{l}</option>)}</select>
        <Button variant="outline" onClick={() => prova({ modo: "esempio", tipo })} disabled={!!busy} data-testid="com-prova-esempio"><Send className="mr-2 h-4 w-4" />Invia esempio a me</Button>
      </div>
      <Tabs defaultValue="registro">
        <TabsList><TabsTrigger value="registro" data-testid="com-tab-registro">Registro</TabsTrigger><TabsTrigger value="impostazioni" data-testid="com-tab-impostazioni">Impostazioni</TabsTrigger><TabsTrigger value="preferenze" data-testid="com-tab-preferenze">Preferenze Partner</TabsTrigger></TabsList>
        <TabsContent value="registro"><Registro tick={k} /></TabsContent>
        <TabsContent value="impostazioni"><Impostazioni /></TabsContent>
        <TabsContent value="preferenze"><PerPartner /><Preferenze /></TabsContent>
      </Tabs>
    </div>
  );
}
